<template>
    <section ref="sectionRef">        
                <div
                    class="col-span-2 lg:col-span-10 lg:col-start-2">
                    <ScriptYouTubePlayer ref="video" :video-id="videoId" @ready="isLoaded = true"
                        @state-change="stateChange">
                        <template #awaitingLoad>
                            <div
                                class="absolute z-80 left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 h-[48px] w-[68px]">
                                <svg height="100%" version="1.1" viewBox="0 0 68 48" width="100%">
                                    <path
                                        d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                                        fill="#f00" />
                                    <path d="M 45,24 27,14 27,34" fill="#fff" />
                                </svg>
                            </div>    
                        </template>
                    </ScriptYouTubePlayer>
                </div>
        
    </section>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, onUnmounted } from 'vue'

import { useWindowSize } from '@vueuse/core'


const { width } = useWindowSize()

const sectionRef = ref<HTMLElement | null>(null)

const isMobile = computed(() => width.value < 800)


const isLoaded = ref(false)
const isPlaying = ref(false)
const video = ref()

const props = defineProps({
    article: {
        type: Object,
        required: true
    }
});

const videoId = computed(() => {
    const content = props.article?.excerpt || ''

    // Find full youtube.com/watch URLs and parse their v= param
    const watchUrls = content.match(/https?:\/\/(?:www\.)?youtube\.com\/watch\?[^"'>\s]*/g) || []
    for (const url of watchUrls) {
        try {
            const u = new URL(url)
            const v = u.searchParams.get('v')
            if (v && /^[A-Za-z0-9_-]{11}$/.test(v)) return v
        } catch {}
    }

    // Short youtu.be URLs
    const shortMatch = content.match(/https?:\/\/(?:www\.)?youtu\.be\/([A-Za-z0-9_-]{11})/i)
    if (shortMatch) return shortMatch[1]

    // Fallback: any v= parameter in the content
    const anyV = content.match(/[?&]v=([A-Za-z0-9_-]{11})/)
    if (anyV) return anyV[1]

    return ''
})

async function play() {
    await video.value.player.playVideo()
}

async function pauseVideo() {
    try {
        await video.value.player.pauseVideo()
    } catch (error) {
        //console.error("Error pausing video:", error)
    }
}

function stateChange(event) {
    /*
    -1 (unstarted)
    0 (ended)
    1 (playing)
    2 (paused)
    3 (buffering)
    5 (video cued).
    */

    console.log("Video state changed:", event.data)
    isPlaying.value = event.data === 1

    if (event.data === 0) {
        // video ended
        isPlaying.value = false
        resetPlayerToPoster()

    }
}

async function resetPlayerToPoster() {
    // Try to reset to the original "uncued" poster + play button.
    // cueVideoById will load the thumbnail and NOT autoplay (shows play button).
    try {
        const p = video.value?.player
        if (!p) return

        // Prefer cueVideoById if available (resets to poster/play button)
        if (typeof p.cueVideoById === 'function') {
            // some implementations accept string or object; object is safe.
            p.cueVideoById({ videoId: props.videoId, startSeconds: 0 })
            // ensure paused state
            if (typeof p.pauseVideo === 'function') p.pauseVideo()
            return
        }

        // Fallback: seek to 0 and pause (may keep end-state in some players)
        if (typeof p.seekTo === 'function') {
            p.seekTo(0)
            if (typeof p.pauseVideo === 'function') p.pauseVideo()
        }

        // Final fallback: stopVideo (IFrame API) – may also show thumbnail
        if (typeof p.stopVideo === 'function') {
            p.stopVideo()
        }
    } catch (e) {
        console.error('resetPlayerToPoster failed', e)
    }
}
</script>
